import { useEffect, useState } from 'react';
import type { ApiClient, UserListRow } from '../api';
import { formatDate, statusTag, renderError } from './shared';

interface Props {
  client: ApiClient;
  onOpen: (id: string) => void;
}

export function UsersView({ client, onOpen }: Props) {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState<string | undefined>(undefined);
  const [users, setUsers] = useState<UserListRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setUsers(null);
    client
      .searchUsers(submitted)
      .then((response) => {
        if (!cancelled) setUsers(response.users);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [client, submitted]);

  return (
    <>
      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(query.trim() || undefined);
        }}
      >
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by email, name, Logto id, or account UUID"
          autoFocus
        />
        <button type="submit">Search</button>
        {submitted && (
          <button
            type="button"
            className="link"
            onClick={() => {
              setQuery('');
              setSubmitted(undefined);
            }}
          >
            Clear
          </button>
        )}
      </form>

      {error !== null && renderError(error)}

      <div className="panel">
        {users === null ? (
          <p className="muted">Loading…</p>
        ) : users.length === 0 ? (
          <p className="muted">No accounts match.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Display name</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="clickable" onClick={() => onOpen(user.id)}>
                  <td>{user.email ?? <span className="muted">—</span>}</td>
                  <td>{user.displayName ?? <span className="muted">—</span>}</td>
                  <td>{statusTag(user.status)}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>{formatDate(user.lastLoginAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
